import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, Leaf, Recycle, Lightning, Mountains } from "@phosphor-icons/react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { Reveal, Eyebrow, SectionHead, fadeUp } from "../components/Motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_IMG =
  "https://images.unsplash.com/photo-1418065460487-3e41a6c84dc5?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";
const ABOUT_IMG = `${BACKEND_URL}/api/static/images/elephant-trunk-raised.png`;
const SECTOR_IMGS = {
  clean_tech:
    "https://customer-assets.emergentagent.com/job_family-equity-hub/artifacts/32lda9ou_image.png",
  biogas:
    "https://images.unsplash.com/photo-1673208769691-e74104d853fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  recycling:
    "https://static.prod-images.emergentagent.com/jobs/1cfd506a-8d58-4dee-aebc-bac5c6ae0bd8/images/8438f6afb400f79a0d8e17138d8d9a7415b8daac32bf69da96a6a7df0bf82582.png",
  natural_resources:
    "https://images.unsplash.com/photo-1448375240586-882707db888b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};
const SECTOR_ICONS = {
  clean_tech: Lightning,
  biogas: Leaf,
  recycling: Recycle,
  natural_resources: Mountains,
};

export default function Home() {
  const { t, lang } = useI18n();
  const [portfolio, setPortfolio] = useState([]);

  useEffect(() => {
    api.get("/portfolio").then((r) => setPortfolio(r.data || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        className="relative min-h-[100vh] flex items-center overflow-hidden pt-28 pb-20 sm:pb-28"
        data-testid="hero-section"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Sustainable industrial building integrated with forest"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/85 via-[#1A1A1A]/30 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/40 to-transparent" />
        </div>

        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              <Eyebrow color="red" className="text-[#E0A6A6]">
                {t.hero.eyebrow}
              </Eyebrow>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 1.2, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light tracking-tighter leading-[1.02] mt-6"
              data-testid="hero-title"
            >
              {t.hero.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="text-base sm:text-lg lg:text-xl text-white/85 max-w-2xl mt-8 leading-relaxed"
            >
              {t.hero.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 mt-10"
            >
              <Link
                to="/philosophy"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#A6192E] hover:bg-[#8C1527] text-white text-sm font-medium tracking-wide rounded-sm transition-all hover:-translate-y-0.5"
                data-testid="hero-cta-philosophy"
              >
                {t.cta.explorePhilosophy}
                <ArrowRight size={16} weight="bold" />
              </Link>
              <Link
                to="/portfolio"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-white/40 text-white hover:bg-white hover:text-[#1A1A1A] text-sm font-medium tracking-wide rounded-sm transition-all"
                data-testid="hero-cta-portfolio"
              >
                {t.cta.seePortfolio}
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mt-16 sm:mt-24 grid grid-cols-3 gap-6 sm:gap-12 max-w-3xl glass rounded-sm p-6 sm:p-8"
            data-testid="hero-stats"
          >
            <div>
              <div className="font-display text-3xl sm:text-4xl text-[#1A1A1A] tracking-tight">
                2006
              </div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#4A4D4A] mt-2">
                {t.hero.stat1}
              </div>
            </div>
            <div>
              <div className="font-display text-3xl sm:text-4xl text-[#1A1A1A] tracking-tight">
                €100M+
              </div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#4A4D4A] mt-2">
                {t.hero.stat2}
              </div>
            </div>
            <div>
              <div className="font-display text-3xl sm:text-4xl text-[#1A1A1A] tracking-tight">
                3
              </div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#4A4D4A] mt-2">
                {t.hero.stat3}
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="absolute right-6 md:right-10 bottom-10 hidden md:flex items-center gap-3 text-white/70 text-xs tracking-[0.2em] uppercase"
        >
          <span>Scroll</span>
          <ArrowDown size={14} weight="regular" />
        </motion.div>
      </section>

      {/* PHILOSOPHY */}
      <section
        className="relative py-24 sm:py-32 px-6 md:px-10"
        data-testid="philosophy-section"
      >
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <SectionHead
              eyebrow={t.philosophy.eyebrow}
              title={t.philosophy.title}
              lead={t.philosophy.lead}
            />
            <Reveal delay={0.2} className="mt-8">
              <Link
                to="/philosophy"
                className="inline-flex items-center gap-2 text-sm font-medium tracking-wide text-[#A6192E] link-underline"
              >
                {t.cta.learnMore} <ArrowRight size={14} weight="bold" />
              </Link>
            </Reveal>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {t.philosophy.pillars.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.08}>
                <div className="h-full p-7 bg-white border border-[#EBEAE0] rounded-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500">
                  <div className="font-mono text-xs text-[#A6192E] tracking-wider">
                    0{i + 1}
                  </div>
                  <h3 className="font-display text-xl text-[#1A1A1A] mt-4 mb-3">{p.t}</h3>
                  <p className="text-sm text-[#4A4D4A] leading-relaxed">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRACK RECORD */}
      <section
        className="relative py-24 sm:py-32 px-6 md:px-10 bg-[#1A4331] text-white grain overflow-hidden"
        data-testid="track-record-section"
      >
        <div className="relative z-10 max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <Eyebrow color="red" className="text-[#E0A6A6]">
              {t.track.eyebrow}
            </Eyebrow>
            <motion.h2
              {...fadeUp}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter mt-6 leading-[1.05]"
            >
              {t.track.title}
            </motion.h2>
            <motion.p
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="text-base sm:text-lg text-white/80 mt-6 max-w-xl"
            >
              {t.track.lead}
            </motion.p>
          </div>
          <div className="lg:col-span-6 grid grid-cols-2 gap-5">
            {t.track.kpi.slice(0, 2).map((k, i) => (
              <Reveal key={k.l} delay={i * 0.08}>
                <div className="p-7 border border-white/10 rounded-sm bg-white/5 backdrop-blur-md">
                  <div className="font-display text-4xl sm:text-5xl font-light tracking-tighter">
                    {k.k}
                  </div>
                  <div className="text-xs uppercase tracking-[0.18em] text-white/60 mt-3">
                    {k.l}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTORS BENTO */}
      <section
        className="relative py-24 sm:py-32 px-6 md:px-10"
        data-testid="sectors-section"
      >
        <div className="max-w-[1400px] mx-auto">
          <SectionHead
            eyebrow={t.sectors.eyebrow}
            title={t.sectors.title}
            lead={t.sectors.lead}
          />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-5">
            {t.sectors.list.map((s, i) => {
              const Icon = SECTOR_ICONS[s.key];
              const span = i === 0 || i === 3 ? "md:col-span-7" : "md:col-span-5";
              return (
                <Reveal key={s.key} delay={i * 0.1} className={span}>
                  <div className="group relative overflow-hidden rounded-sm h-[320px] sm:h-[380px] border border-[#EBEAE0]">
                    <img
                      src={SECTOR_IMGS[s.key]}
                      alt={s.t}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                    <div className="absolute inset-0 p-7 flex flex-col justify-between text-white">
                      <div className="flex items-center gap-3">
                        <Icon size={28} weight="duotone" className="text-[#E0A6A6]" />
                        <span className="text-xs tracking-[0.2em] uppercase text-white/70">
                          0{i + 1}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-display text-2xl sm:text-3xl font-medium tracking-tight">
                          {s.t}
                        </h3>
                        <p className="text-sm text-white/80 mt-3 max-w-md leading-relaxed">
                          {s.d}
                        </p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.2} className="mt-12">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs tracking-[0.22em] uppercase text-[#A6192E] mr-2">
                {t.sectors.geoTitle}
              </span>
              {t.sectors.geo.map((g) => (
                <span
                  key={g}
                  className="px-4 py-2 border border-[#1A1A1A]/15 text-sm text-[#1A1A1A] rounded-sm bg-white"
                >
                  {g}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="relative py-24 sm:py-32 px-6 md:px-10 bg-[#F3F2EB]">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-6 order-2 lg:order-1">
            <Eyebrow>{t.approach.eyebrow}</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter mt-6 leading-[1.05] text-[#1A1A1A]">
              {t.approach.title}
            </h2>
            <p className="text-base sm:text-lg text-[#4A4D4A] mt-6 max-w-xl leading-relaxed">
              {t.approach.lead}
            </p>
            <ul className="mt-10 space-y-5 max-w-xl">
              {t.approach.pillars.map((p, i) => (
                <li
                  key={p.t}
                  className="flex gap-5 pb-5 border-b border-[#1A1A1A]/10 last:border-none"
                >
                  <span className="font-mono text-xs text-[#A6192E] mt-1 tracking-wider min-w-[28px]">
                    0{i + 1}
                  </span>
                  <div>
                    <h3 className="font-display text-lg text-[#1A1A1A]">{p.t}</h3>
                    <p className="text-sm text-[#4A4D4A] mt-1.5 leading-relaxed">{p.d}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.2} className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative">
              <div className="relative overflow-hidden rounded-sm aspect-[4/5]">
                <img
                  src={ABOUT_IMG}
                  alt="Elephant herd in silhouette at warm sunset"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-4 sm:-left-8 glass rounded-sm p-6 sm:p-7 max-w-xs">
                <div className="font-display text-2xl text-[#1A4331] tracking-tight leading-tight">
                  &ldquo;Responsibility over generations.&rdquo;
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#A6192E] mt-3">
                  Guiding principle
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CINEMATIC VIDEO BAND */}
      <section
        className="relative py-24 sm:py-32 px-6 md:px-10"
        data-testid="video-band-section"
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-3xl mb-12">
            <Eyebrow color="green">From the field</Eyebrow>
            <motion.h2
              {...fadeUp}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter mt-6 leading-[1.05] text-[#1A1A1A]"
            >
              The transition, in motion.
            </motion.h2>
            <motion.p
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="text-base sm:text-lg text-[#4A4D4A] mt-6 max-w-2xl leading-relaxed"
            >
              From clean-energy generation to closing the loop on plastic waste —
              the industries we own are building the resource-efficient economy of
              the next generation.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Reveal>
              <div className="group relative overflow-hidden rounded-sm aspect-[4/3] sm:aspect-[16/10] bg-[#1A1A1A]">
                <video
                  src={`${BACKEND_URL}/api/static/videos/wind-farm.mp4`}
                  poster="https://images.unsplash.com/photo-1759800713796-1bbc33a51e2f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="video-wind-farm"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none" />
                <div className="absolute inset-0 p-7 sm:p-9 flex flex-col justify-between text-white">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-2 w-2 rounded-full bg-[#E0A6A6] animate-pulse" />
                    <span className="text-[11px] tracking-[0.22em] uppercase text-white/80">
                      Clean technologies · Aerial
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display text-2xl sm:text-3xl tracking-tight">
                      Wind farms, at scale.
                    </h3>
                    <p className="text-sm text-white/75 mt-2 max-w-md">
                      The infrastructure of the energy transition — owned and
                      operated with long horizons.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="group relative overflow-hidden rounded-sm aspect-[4/3] sm:aspect-[16/10] bg-[#1A1A1A]">
                <video
                  src={`${BACKEND_URL}/api/static/videos/plastic-waste.mp4`}
                  poster="https://static.prod-images.emergentagent.com/jobs/1cfd506a-8d58-4dee-aebc-bac5c6ae0bd8/images/8438f6afb400f79a0d8e17138d8d9a7415b8daac32bf69da96a6a7df0bf82582.png"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="video-plastic-waste"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none" />
                <div className="absolute inset-0 p-7 sm:p-9 flex flex-col justify-between text-white">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-2 w-2 rounded-full bg-[#E0A6A6] animate-pulse" />
                    <span className="text-[11px] tracking-[0.22em] uppercase text-white/80">
                      Circular economy · Field
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display text-2xl sm:text-3xl tracking-tight">
                      From waste to resource.
                    </h3>
                    <p className="text-sm text-white/75 mt-2 max-w-md">
                      Plastics, metals and natural materials — kept in the loop
                      and out of the environment.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SME BUILDER SHOWCASE */}
      <section
        className="relative py-24 sm:py-32 px-6 md:px-10 bg-[#1A1A1A] text-white overflow-hidden"
        data-testid="sme-builder-section"
      >
        <div className="relative z-10 max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <span className="text-xs sm:text-sm font-semibold tracking-[0.22em] uppercase text-[#E0A6A6]">
                Investor & Company Builder
              </span>
              <motion.h2
                {...fadeUp}
                className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[1.05] mt-6 text-white"
              >
                We invest in — and build —
                <br />
                <span className="text-[#E0A6A6]">SME champions.</span>
              </motion.h2>
              <motion.p
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.1 }}
                className="text-base sm:text-lg text-white/75 mt-6 max-w-2xl leading-relaxed"
              >
                Our focus is on small and medium-sized enterprises with strong
                fundamentals and untapped potential. We don't just write a cheque —
                we work alongside management to build market leaders that endure.
              </motion.p>
            </div>
            <div className="lg:col-span-5 grid grid-cols-3 gap-2">
              {[
                { k: "20+", l: "Years building SMEs" },
                { k: "€100M+", l: "Capital deployed" },
                { k: "4", l: "Sectors of focus" },
              ].map((s, i) => (
                <Reveal key={s.l} delay={i * 0.08}>
                  <div className="p-5 bg-white/5 border border-white/10 rounded-sm h-full backdrop-blur-md">
                    <div className="font-display text-2xl sm:text-3xl font-light tracking-tighter text-white">
                      {s.k}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/55 mt-2 leading-snug">
                      {s.l}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                t: "Capital",
                d: "Permanent equity from our own balance sheet — no fund deadlines, no LP pressure.",
              },
              {
                t: "Operating partnership",
                d: "Hands-on involvement: strategy, finance, governance and international expansion.",
              },
              {
                t: "Global footprint",
                d: "Offices in Pune and Dubai — supporting SMEs across Europe, the US, Asia and the Middle East.",
              },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 0.08}>
                <div className="p-7 bg-white/5 border border-white/10 rounded-sm h-full">
                  <div className="font-mono text-xs text-[#E0A6A6] tracking-wider">
                    0{i + 1}
                  </div>
                  <h3 className="font-display text-xl text-white mt-4 tracking-tight">
                    {c.t}
                  </h3>
                  <p className="text-sm text-white/70 mt-3 leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO TEASER */}
      <section
        className="relative py-24 sm:py-32 px-6 md:px-10"
        data-testid="portfolio-teaser"
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
            <SectionHead
              eyebrow={t.portfolio.eyebrow}
              title={t.portfolio.title}
              lead={t.portfolio.lead}
            />
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-2 text-sm font-medium tracking-wide text-[#A6192E] link-underline self-start sm:self-end"
              data-testid="portfolio-view-all"
            >
              {t.cta.viewAll} <ArrowRight size={14} weight="bold" />
            </Link>
          </div>

          {portfolio.length === 0 ? (
            <div className="text-sm text-[#7A7D7A]">{t.portfolio.empty}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {portfolio.slice(0, 3).map((p, i) => (
                <Reveal key={p.id} delay={i * 0.1}>
                  <div className="group bg-white border border-[#EBEAE0] rounded-sm overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-500">
                    <div className="aspect-[5/3] overflow-hidden bg-white border-b border-[#EBEAE0] flex items-center justify-center p-8">
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt={p.name}
                          className="max-w-full max-h-full object-contain transition-transform duration-[1200ms] group-hover:scale-105"
                        />
                      ) : (
                        <div className="font-display text-3xl text-[#1A1A1A]/30 tracking-tight text-center px-4">
                          {p.name}
                        </div>
                      )}
                    </div>
                    <div className="p-7">
                      <div className="text-xs tracking-[0.18em] uppercase text-[#A6192E]">
                        {t.sectorLabels[p.sector] || p.sector}
                      </div>
                      <h3 className="font-display text-2xl text-[#1A1A1A] mt-3 tracking-tight">
                        {p.name}
                      </h3>
                      <p className="text-sm text-[#4A4D4A] mt-3 leading-relaxed line-clamp-3">
                        {(lang === "de" && p.description_de) || p.description_en}
                      </p>
                      <div className="flex items-center justify-between mt-5 text-xs text-[#7A7D7A]">
                        <div className="flex items-center gap-3 font-mono">
                          <span>{p.country}</span>
                          {p.year_invested && (
                            <>
                              <span className="text-[#1A1A1A]/20">·</span>
                              <span>since {p.year_invested}</span>
                            </>
                          )}
                        </div>
                        {p.website && (
                          <a
                            href={p.website}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="text-[#A6192E] link-underline font-mono"
                          >
                            Visit ↗
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
